<template>
  <section class="how">
    <div class="container">
      <!-- En-tête commun -->
      <div class="how__header">
        <h2 class="how__title">{{ t('how.title') }}</h2>
        <p class="how__subtitle">{{ t('how.subtitle') }}</p>
      </div>

      <!-- STEP 1 -->
      <div class="how__step">
        <div class="how__chat">
          <div class="how__msg-group">
            <NuxtImg
              src="/images/lola-avatar.png"
              alt="Lola"
              class="how__avatar how__avatar--lola"
              width="44"
              height="44"
              loading="lazy"
              decoding="async"
              format="webp"
              @error="onChatAvatarError" />
            <div class="how__avatar how__avatar--lola how__avatar--fallback">✨</div>
            <div class="how__bubble how__bubble--lola">
              <p>{{ t('how.step1.chat.lola1') }}</p>
            </div>
          </div>
          <div class="how__msg-group how__msg-group--user">
            <div class="how__bubble how__bubble--user">
              <p>{{ t('how.step1.chat.user') }}</p>
            </div>
            <NuxtImg
              src="/images/user-pauline.jpg"
              alt="Pauline"
              class="how__avatar how__avatar--user"
              width="44"
              height="44"
              loading="lazy"
              decoding="async"
              format="webp"
              @error="onChatAvatarError" />
            <div
              class="how__avatar how__avatar--user how__avatar--fallback how__avatar--user-fallback">
              P
            </div>
          </div>
          <div class="how__msg-group">
            <NuxtImg
              src="/images/lola-avatar.png"
              alt="Lola"
              class="how__avatar how__avatar--lola"
              width="44"
              height="44"
              loading="lazy"
              decoding="async"
              format="webp"
              @error="onChatAvatarError" />
            <div class="how__avatar how__avatar--lola how__avatar--fallback">✨</div>
            <div class="how__bubble how__bubble--lola">
              <p>{{ t('how.step1.chat.lola2') }}</p>
            </div>
          </div>
        </div>
        <div class="how__step-desc">
          <p class="how__step-label">
            <span class="how__step-num">{{ t('how.step1.label') }}</span> {{ t('how.step1.title') }}
          </p>
          <p class="how__step-text">{{ t('how.step1.desc1') }}</p>
          <p class="how__step-text">{{ t('how.step1.desc2') }}</p>
        </div>
      </div>

      <!-- STEP 2 -->
      <div class="how__step how__step--reverse how__step--spaced">
        <div class="how__step-desc">
          <p class="how__step-label">
            <span class="how__step-num">{{ t('how.step2.label') }}</span> {{ t('how.step2.title') }}
          </p>
          <p class="how__step-text">{{ t('how.step2.desc1') }}</p>
          <p class="how__step-text">{{ t('how.step2.desc2') }}</p>
        </div>
        <div class="how__profile-cards">
          <div class="how__pcard">
            <div class="how__pcard-accent" />
            <div class="how__pcard-header">
              <p class="how__pcard-title">{{ t('how.step2.mbti.title') }}</p>
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                class="how__pcard-chevron">
                <path
                  d="M3 10l5-5 5 5"
                  stroke="currentColor"
                  stroke-width="1.6"
                  stroke-linecap="round"
                  stroke-linejoin="round" />
              </svg>
            </div>
            <PrePeersMbtiChip
              code="ENFP"
              class="how__mbti-chip-wrap" />
            <ul class="how__mbti-traits">
              <li
                v-for="(trait, i) in mbtiTraits"
                :key="i">
                <span class="how__trait-dot">◇</span>
                <span v-html="trait" />
              </li>
            </ul>
          </div>
          <div class="how__pcard">
            <div class="how__pcard-accent" />
            <div class="how__pcard-header">
              <p class="how__pcard-title">{{ t('how.step2.shadow.title') }}</p>
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                class="how__pcard-chevron">
                <path
                  d="M3 10l5-5 5 5"
                  stroke="currentColor"
                  stroke-width="1.6"
                  stroke-linecap="round"
                  stroke-linejoin="round" />
              </svg>
            </div>
            <ul class="how__shadow-list">
              <li
                v-for="(item, i) in shadowItems"
                :key="i">
                <span class="how__shadow-icon">⚠️</span><span>{{ item }}</span>
              </li>
            </ul>
            <p class="how__shadow-note">{{ t('how.step2.shadow.note') }}</p>
          </div>
          <div class="how__pcard how__pcard--desktop-only">
            <div class="how__pcard-accent" />
            <div class="how__pcard-header">
              <p class="how__pcard-title">{{ t('how.step2.cocktail.title') }}</p>
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                class="how__pcard-chevron">
                <path
                  d="M3 10l5-5 5 5"
                  stroke="currentColor"
                  stroke-width="1.6"
                  stroke-linecap="round"
                  stroke-linejoin="round" />
              </svg>
            </div>
            <p class="how__cocktail-text">{{ t('how.step2.cocktail.desc') }}</p>
          </div>
          <div class="how__pcard how__pcard--mobile-only">
            <div class="how__pcard-accent how__pcard-accent--green" />
            <div class="how__pcard-header">
              <p class="how__pcard-title">{{ t('how.step2.confidence.title') }}</p>
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                class="how__pcard-chevron">
                <path
                  d="M3 10l5-5 5 5"
                  stroke="currentColor"
                  stroke-width="1.6"
                  stroke-linecap="round"
                  stroke-linejoin="round" />
              </svg>
            </div>
            <div class="how__confidence-row">
              <div class="how__confidence-gauge">
                <svg
                  viewBox="0 0 80 50"
                  width="80"
                  height="50">
                  <path
                    d="M 8 44 A 32 32 0 0 1 72 44"
                    fill="none"
                    stroke="rgba(255,255,255,0.08)"
                    stroke-width="6"
                    stroke-linecap="round" />
                  <path
                    :d="confidenceArc"
                    fill="none"
                    stroke="url(#confGrad)"
                    stroke-width="6"
                    stroke-linecap="round" />
                  <defs>
                    <linearGradient
                      id="confGrad"
                      x1="0"
                      y1="0"
                      x2="1"
                      y2="0">
                      <stop
                        offset="0%"
                        stop-color="#10b981" />
                      <stop
                        offset="100%"
                        stop-color="#06b6d4" />
                    </linearGradient>
                  </defs>
                </svg>
                <span class="how__confidence-value">80%</span>
              </div>
              <div class="how__confidence-text">
                <p class="how__confidence-level">{{ t('how.step2.confidence.level') }}</p>
                <p class="how__confidence-desc">{{ t('how.step2.confidence.desc') }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- STEP 3 -->
      <div class="how__step how__step--spaced how__step--start how__step--wide">
        <!-- Colonne gauche : programmes + métiers -->
        <div class="how__s3-visual">
          <!-- Programmes -->
          <div class="how__s3-col">
            <span class="how__s3-badge how__s3-badge--orange">3 programmes</span>
            <div class="how__s3-cards">
              <div
                v-for="(prog, i) in programmes"
                :key="prog.title"
                class="how__s3-card"
                :class="{ 'how__s3-card--best': i === 0 }">
                <span
                  v-if="i === 0"
                  class="how__s3-best"
                  >Meilleur match</span
                >
                <div class="how__s3-card-inner">
                  <div class="how__s3-score">
                    <svg
                      viewBox="0 0 40 40"
                      width="40"
                      height="40">
                      <circle
                        cx="20"
                        cy="20"
                        r="16"
                        fill="none"
                        stroke="rgba(255,255,255,0.08)"
                        stroke-width="5.5" />
                      <circle
                        cx="20"
                        cy="20"
                        r="16"
                        fill="none"
                        stroke="url(#s3g)"
                        stroke-width="5.5"
                        :stroke-dasharray="`${(100.5 * prog.score) / 100} 100.5`"
                        stroke-dashoffset="25.1"
                        stroke-linecap="round" />
                      <defs>
                        <linearGradient
                          id="s3g"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1">
                          <stop
                            offset="0%"
                            stop-color="#10b981" />
                          <stop
                            offset="100%"
                            stop-color="#06b6d4" />
                        </linearGradient>
                      </defs>
                    </svg>
                    <span class="how__s3-score-val">{{ prog.score }}</span>
                  </div>
                  <div class="how__s3-info">
                    <p class="how__s3-name">{{ prog.title }}</p>
                    <p class="how__s3-sub">{{ prog.school }}</p>
                    <p class="how__s3-loc">📍 {{ prog.loc }}</p>
                  </div>
                  <div class="how__s3-actions">
                    <button class="how__s3-btn-icon">♡</button>
                    <button class="how__s3-btn-icon">∨</button>
                  </div>
                </div>
              </div>
            </div>
            <NuxtLink
              to="/ecoles"
              class="btn btn-outline btn-sm how__s3-link">
              {{ t('how.step3.compareBtn') }}
            </NuxtLink>
          </div>

          <!-- Métiers — desktop uniquement -->
          <div class="how__s3-col how__s3-col--desktop-only">
            <span class="how__s3-badge how__s3-badge--violet">3 métiers</span>
            <div class="how__s3-cards">
              <div
                v-for="(job, i) in jobs"
                :key="job.title"
                class="how__s3-card"
                :class="{ 'how__s3-card--best': i === 0 }">
                <span
                  v-if="i === 0"
                  class="how__s3-best"
                  >Meilleur match</span
                >
                <div class="how__s3-card-inner">
                  <div class="how__s3-score">
                    <svg
                      viewBox="0 0 40 40"
                      width="40"
                      height="40">
                      <circle
                        cx="20"
                        cy="20"
                        r="16"
                        fill="none"
                        stroke="rgba(255,255,255,0.08)"
                        stroke-width="5.5" />
                      <circle
                        cx="20"
                        cy="20"
                        r="16"
                        fill="none"
                        stroke="url(#s3g2)"
                        stroke-width="5.5"
                        :stroke-dasharray="`${(100.5 * job.score) / 100} 100.5`"
                        stroke-dashoffset="25.1"
                        stroke-linecap="round" />
                      <defs>
                        <linearGradient
                          id="s3g2"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1">
                          <stop
                            offset="0%"
                            stop-color="#10b981" />
                          <stop
                            offset="100%"
                            stop-color="#06b6d4" />
                        </linearGradient>
                      </defs>
                    </svg>
                    <span class="how__s3-score-val">{{ job.score }}</span>
                  </div>
                  <div class="how__s3-info">
                    <p class="how__s3-name">{{ job.title }}</p>
                    <p class="how__s3-sub">{{ job.sector }}</p>
                    <p class="how__s3-loc">💰 {{ job.salary }}</p>
                  </div>
                  <div class="how__s3-actions">
                    <button class="how__s3-btn-icon">♡</button>
                    <button class="how__s3-btn-icon">∨</button>
                  </div>
                </div>
              </div>
            </div>
            <NuxtLink
              to="/metiers"
              class="btn btn-outline btn-sm how__s3-link how__s3-link--violet">
              {{ t('how.step3.exploreBtn') }}
            </NuxtLink>
          </div>
        </div>

        <!-- Colonne droite : description -->
        <div class="how__step-desc">
          <p class="how__step-label">
            <span class="how__step-num">{{ t('how.step3.label') }}</span> {{ t('how.step3.title') }}
          </p>
          <p class="how__step-text">{{ t('how.step3.desc1') }}</p>
          <p class="how__step-text">{{ t('how.step3.desc2') }}</p>
          <NuxtLink
            :to="localePath('/register')"
            class="btn btn-primary btn-lg how__s3-cta">
            {{ t('how.step3.cta') }}
          </NuxtLink>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import PrePeersMbtiChip from '~/components/ui/PrePeersMbtiChip.vue'

const { t } = useI18n()
const localePath = useLocalePath()

/**
 * Masque l'avatar image et affiche le fallback emoji / initiale adjacent.
 * @param {Event} event - Événement `error` de `<NuxtImg>`.
 */
function onChatAvatarError(event: Event): void {
  const img: HTMLImageElement | null = event.target as HTMLImageElement | null
  if (!img) return
  img.style.display = 'none'
  const fallback: HTMLElement | null = img.nextElementSibling as HTMLElement | null
  if (fallback) fallback.style.display = 'flex'
}

const mbtiTraits = [
  '<strong>Créatif(ve) et enthousiaste</strong> : tu transformes les idées en actions avec une spontanéité rare.',
  "<strong>Curieux(se) et plein(e) d'énergie</strong> : tu avances vite et ouvres des portes là où d'autres hésitent.",
  '<strong>Profondément tourné(e) vers les autres</strong> : tu inspires ton entourage et fais naître des opportunités.',
]

const shadowItems = ["Tendance à l'isolement", 'Frustration quand ça manque de rythme']

const confidenceArc = (() => {
  const CX = 40,
    CY = 44,
    R = 32
  const rad = (a) => (a * Math.PI) / 180
  const startAngle = -180
  const angle = startAngle + 180 * (80 / 100)
  const x1 = CX + R * Math.cos(rad(startAngle))
  const y1 = CY + R * Math.sin(rad(startAngle))
  const x2 = CX + R * Math.cos(rad(angle))
  const y2 = CY + R * Math.sin(rad(angle))
  return `M ${x1} ${y1} A ${R} ${R} 0 0 1 ${x2} ${y2}`
})()

const programmes = [
  {
    score: 95,
    title: 'Bachelor BBA',
    school: 'emlyon business school',
    loc: 'Lyon, Paris, Casablanca',
  },
  { score: 92, title: 'Bachelor UX Design', school: 'ESIEA', loc: 'Paris, Laval' },
  { score: 88, title: 'Bachelor Front-End', school: 'ESIEE IT', loc: 'Paris, Pointoise' },
]

const jobs = [
  { score: 95, title: 'UX Designer', sector: 'Numérique & Digital', salary: '2,2k — 5,5k€/mois' },
  {
    score: 92,
    title: 'Chef de projet digital',
    sector: 'Marketing & Tech',
    salary: '2,5k — 5k€/mois',
  },
  {
    score: 88,
    title: 'Consultant en recrutement',
    sector: 'RH & Conseil',
    salary: 'Paris, Pointoise',
  },
]
</script>

<style scoped>
.how {
  background: #151618;
  padding-block: var(--space-20);
}

.how__header {
  text-align: center;
  margin-bottom: var(--space-16);
}

.how__title {
  font-size: clamp(1.5rem, 3vw, 2rem);
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: var(--space-4);
}

.how__subtitle {
  font-size: var(--text-base);
  color: var(--text-secondary);
  line-height: var(--leading-relaxed);
  max-width: 520px;
  margin-inline: auto;
}

/* Step layout */
.how__step {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-16);
  align-items: center;
  max-width: 960px;
  margin-inline: auto;
}

.how__step--reverse .how__step-desc {
  order: 2;
}
.how__step--reverse .how__profile-cards {
  order: 1;
}
.how__step--spaced {
  margin-top: var(--space-20);
}
.how__step--start {
  align-items: start;
}
.how__step--wide {
  max-width: 1100px;
}

/* Chat */
.how__chat {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.how__msg-group {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
}
.how__msg-group--user {
  justify-content: flex-end;
}

.how__msg-group picture {
  flex-shrink: 0;
  display: block;
  line-height: 0;
}

.how__avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}
.how__avatar--lola {
  border: 2px solid var(--accent-blue);
  box-shadow: 0 0 0 3px rgba(3, 160, 241, 0.2);
}
.how__avatar--fallback {
  display: none;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  background: var(--gradient-violet);
}
.how__avatar--user-fallback {
  background: var(--gradient-blue);
  font-size: var(--text-sm);
  font-weight: 700;
  color: white;
}

.how__bubble {
  max-width: 85%;
  padding: var(--space-4) var(--space-5);
  border-radius: var(--radius-lg);
  font-size: var(--text-sm);
  line-height: var(--leading-relaxed);
}
.how__bubble--lola {
  background: #1e1f22;
  border: 1px solid var(--border-subtle);
  border-radius: 0 var(--radius-lg) var(--radius-lg) var(--radius-lg);
  color: var(--text-primary);
}
.how__bubble--user {
  background: linear-gradient(135deg, #03a0f1 0%, #3f3fd8 100%);
  border-radius: var(--radius-lg) 0 var(--radius-lg) var(--radius-lg);
  color: #fff;
}

/* Step desc */
.how__step-desc {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}

.how__step-label {
  font-size: clamp(1.25rem, 2.5vw, 1.75rem);
  font-weight: 700;
  color: var(--text-primary);
  line-height: var(--leading-tight);
}
.how__step-num {
  color: var(--accent-blue);
  font-style: italic;
}
.how__step-text {
  font-size: var(--text-base);
  color: var(--text-secondary);
  line-height: var(--leading-relaxed);
}

/* Profile cards step 2 */
.how__profile-cards {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.how__pcard {
  position: relative;
  background: #1e1f22;
  border-radius: var(--radius-lg);
  padding: var(--space-4) var(--space-5);
  overflow: hidden;
}
.how__pcard-accent {
  position: absolute;
  top: 0;
  right: 0;
  width: 4px;
  height: 100%;
  background: linear-gradient(180deg, #3f3fd8 0%, #03a0f1 100%);
  border-radius: 0 var(--radius-lg) var(--radius-lg) 0;
}
.how__pcard-accent--green {
  background: linear-gradient(180deg, #10b981 0%, #06b6d4 100%);
}
.how__pcard-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--space-3);
}
.how__pcard-title {
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--text-primary);
}
.how__pcard-chevron {
  color: var(--text-muted);
  flex-shrink: 0;
}

.how__mbti-chip-wrap {
  margin-bottom: var(--space-3);
}
.how__mbti-traits {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}
.how__mbti-traits li {
  display: flex;
  gap: var(--space-2);
  font-size: var(--text-xs);
  color: var(--text-secondary);
  line-height: var(--leading-relaxed);
}
.how__trait-dot {
  color: var(--accent-blue-lt);
  flex-shrink: 0;
  font-size: 0.6rem;
  margin-top: 3px;
}

.how__shadow-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  margin-bottom: var(--space-3);
}
.how__shadow-list li {
  display: flex;
  gap: var(--space-2);
  font-size: var(--text-sm);
  color: var(--text-secondary);
}
.how__shadow-icon {
  flex-shrink: 0;
}
.how__shadow-note {
  font-size: var(--text-xs);
  color: var(--text-muted);
  font-style: italic;
  line-height: var(--leading-relaxed);
}
.how__cocktail-text {
  font-size: var(--text-sm);
  color: var(--text-secondary);
}

.how__confidence-row {
  display: flex;
  align-items: center;
  gap: var(--space-4);
}
.how__confidence-gauge {
  position: relative;
  flex-shrink: 0;
  width: 80px;
  height: 50px;
}
.how__confidence-value {
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  font-size: var(--text-base);
  font-weight: 800;
  color: var(--text-primary);
}
.how__confidence-level {
  font-size: var(--text-sm);
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: var(--space-1);
}
.how__confidence-desc {
  font-size: var(--text-xs);
  color: var(--text-secondary);
  line-height: var(--leading-relaxed);
  font-style: italic;
}

.how__pcard--mobile-only {
  display: none;
}
.how__pcard--desktop-only {
  display: block;
}

/* Step 3 */
.how__s3-visual {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-4);
  align-items: start;
}

.how__s3-col {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}
.how__s3-col--desktop-only {
  display: flex;
  margin-top: var(--space-12);
}

.how__s3-badge {
  display: inline-block;
  width: fit-content;
  font-size: var(--text-xs);
  font-weight: 700;
  border-radius: var(--radius-full);
  padding: 0.25em 0.9em;
  color: #fff;
}
.how__s3-badge--orange {
  background: var(--accent-orange);
}
.how__s3-badge--violet {
  background: var(--accent-violet);
}

.how__s3-cards {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.how__s3-card {
  background: #1e1f22;
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-md);
  padding: var(--space-3) var(--space-4);
  position: relative;
  overflow: hidden;
}

.how__s3-card--best {
  border-color: #26b07d;
  box-shadow: 0 0 0 1px #26b07d;
}

.how__s3-best {
  display: block;
  font-size: var(--text-xs);
  font-weight: 600;
  color: #26b07d;
  text-align: center;
  margin-bottom: var(--space-2);
}

.how__s3-card-inner {
  display: flex;
  align-items: center;
  gap: var(--space-3);
}

.how__s3-score {
  position: relative;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.how__s3-score-val {
  position: absolute;
  font-size: 0.6rem;
  font-weight: 800;
  color: var(--text-primary);
}

.how__s3-info {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}
.how__s3-name {
  font-size: var(--text-xs);
  font-weight: 700;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.how__s3-sub {
  font-size: var(--text-xs);
  color: var(--text-secondary);
  margin-top: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.how__s3-loc {
  font-size: var(--text-xs);
  color: var(--text-muted);
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.how__s3-actions {
  display: flex;
  gap: var(--space-1);
  flex-shrink: 0;
}
.how__s3-btn-icon {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-muted);
  font-size: var(--text-sm);
  padding: 2px;
  transition: color var(--duration-fast) var(--ease-out);
}
.how__s3-btn-icon:hover {
  color: var(--text-primary);
}

.how__s3-link {
  width: 100%;
  justify-content: center;
}
.how__s3-link--violet {
  border-color: var(--accent-violet);
  color: var(--accent-violet-lt);
}
.how__s3-link--violet:hover {
  background: rgba(124, 58, 237, 0.1);
}

.how__s3-cta {
  margin-top: var(--space-2);
  width: fit-content;
}

/* Responsive */
@media (max-width: 768px) {
  .how__step {
    grid-template-columns: 1fr;
    gap: var(--space-8);
  }

  .how__step .how__step-desc {
    order: -1;
  }
  .how__step--reverse .how__step-desc {
    order: -1;
  }
  .how__step--reverse .how__profile-cards {
    order: 0;
  }

  .how__step-label {
    font-size: 1.375rem;
  }
  .how__bubble {
    max-width: 90%;
  }

  .how__pcard--mobile-only {
    display: block;
  }
  .how__pcard--desktop-only {
    display: none;
  }

  .how__s3-visual {
    grid-template-columns: 1fr;
  }
  .how__s3-col--desktop-only {
    display: none;
  }

  .how__s3-cta {
    width: 100%;
    justify-content: center;
  }
}
</style>
